import { useMemo, useRef } from "react";

import { filterFactory } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import { BrandsFilter } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/brands-filter/BrandsFilter";
import { VISIBILITY_RANGE } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/consts";
import { usePromptsUrlParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/hooks/usePromptsUrlParams";
import { MultiselectFilterOption } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/MultiselectFilterOption";
import {
    CITATION_GAP_OPTIONS,
    CITATION_VOLATILITY_OPTIONS,
    LATEST_CITATION_STATUS_OPTIONS,
    MENTION_STATUS_OPTIONS,
    SENTIMENT_TONE_OPTIONS,
    TOP_SOURCE_CATEGORY_OPTIONS,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/promptsFilterOptions";
import type { IPromptsFilterState } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";
import { ChangeStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const Filters = filterFactory.createTypedFilters<IPromptsFilterState>();

type MultiSelectOption = filterFactory.MultiSelectOption;
type RangeValue = filterFactory.RangeValue;

interface IPromptsDataFiltersProps {
    isLoading?: boolean;
    hasPriorWindow?: boolean;
    coreCompetitorBrands?: string[];
    isCoreCompetitorsSelected?: boolean;
    brandsOptions: MultiSelectOption[];
    domainsOptions: MultiSelectOption[];
}

const buildFilterUrlUpdates = (newFilterState: IPromptsFilterState): Record<string, string | undefined> => ({
    quickFilter: undefined,
    mentionStatuses: newFilterState.mentionSentimentStatus?.length
        ? newFilterState.mentionSentimentStatus.join(",")
        : undefined,
    minVisibility: newFilterState.visibility?.from !== undefined ? String(newFilterState.visibility.from) : undefined,
    maxVisibility: newFilterState.visibility?.to !== undefined ? String(newFilterState.visibility.to) : undefined,
    brands: newFilterState.brands?.length ? newFilterState.brands.join(",") : undefined,
    domains: newFilterState.domains?.length ? newFilterState.domains.join(",") : undefined,
    topSourceCategories: newFilterState.topSourceCategories?.length
        ? newFilterState.topSourceCategories.join(",")
        : undefined,
    citationGaps: newFilterState.citationGap?.length ? newFilterState.citationGap.join(",") : undefined,
    citationVolatility: newFilterState.citationVolatility?.length
        ? newFilterState.citationVolatility.join(",")
        : undefined,
    sentimentTone: newFilterState.sentimentTone?.length ? newFilterState.sentimentTone.join(",") : undefined,
    latestCitationStatuses: newFilterState.latestCitationStatus?.length
        ? newFilterState.latestCitationStatus.join(",")
        : undefined,
    change: newFilterState.change?.length ? newFilterState.change.join(",") : undefined,
    sentimentShift: newFilterState.sentimentShift?.length ? newFilterState.sentimentShift.join(",") : undefined,
});

const updateUrlParams = (newFilterState: IPromptsFilterState) => {
    void proNavigator.updateParams(buildFilterUrlUpdates(newFilterState), { reload: false });
};

const clearAllFilterParams = () => {
    void proNavigator.updateParams({ ...buildFilterUrlUpdates({}), coreCompetitors: undefined }, { reload: false });
};

const createSelectorFilterLabel = (baseLabel: string, selectedText: string) => (value: string[] | undefined) => {
    if (!value?.length) {
        return baseLabel;
    }

    if (value.length === 1) {
        return `${baseLabel}: ${value[0]}`;
    }

    return `${baseLabel}: ${value.length} ${selectedText}`;
};

const createRangeFilterLabel = (baseLabel: string, range: RangeValue) => (value: RangeValue) => {
    if (!value || (value.from === undefined && value.to === undefined)) {
        return baseLabel;
    }

    const from = value.from ?? range.from;
    const to = value.to ?? range.to;
    return `${baseLabel}: ${from}-${to}`;
};

export const PromptsDataFilters = ({
    isLoading = false,
    hasPriorWindow = true,
    coreCompetitorBrands,
    isCoreCompetitorsSelected = false,
    brandsOptions,
    domainsOptions,
}: IPromptsDataFiltersProps) => {
    const { filterState } = usePromptsUrlParams();
    const latestFilterStateRef = useRef<IPromptsFilterState>(filterState);

    const handleProviderChange = (newState: IPromptsFilterState) => {
        latestFilterStateRef.current = newState;
        updateUrlParams(newState);
    };

    const handleBrandsSelectionChange = (brands: string[], isCoreCompetitorsSelected: boolean) => {
        void proNavigator.updateParams(
            {
                quickFilter: undefined,
                brands: brands.length ? brands.join(",") : undefined,
                coreCompetitors: isCoreCompetitorsSelected ? "true" : undefined,
            },
            { reload: false },
        );
    };

    const mentionStatusFilterLabel = translate("llm.prompts.filters.mention_status.title");
    const visibilityFilterLabel = translate("llm.url_analysis.filters.visibility.title");
    const brandsFilterLabel = translate("llm.url_analysis.filters.brands.title");
    const domainsFilterLabel = translate("llm.prompts.filters.cited_domains.title");
    const topSourceCategoryFilterLabel = translate("llm.prompts.filters.top_source_category.title");
    const citationGapFilterLabel = translate("llm.prompts.filters.citation_gap.title");
    const citationVolatilityFilterLabel = translate("llm.prompts.filters.citation_volatility.title");
    const sentimentFilterLabel = translate("llm.prompts.filters.sentiment.title");
    const latestCitationStatusFilterLabel = translate("llm.prompts.filters.latest_citation_status.title");
    const changeFilterLabel = translate("llm.prompts.filters.change.title");
    const sentimentShiftFilterLabel = translate("llm.prompts.filters.sentiment_shift.title");
    const selectedText = translate("llm.url_analysis.filters.selected");
    const domainsPlaceholder = translate("llm.prompts.filters.cited_domains.search_placeholder");
    const topSourceCategoryPlaceholder = translate("llm.prompts.filters.top_source_category.search_placeholder");

    const changeOptions = useMemo<MultiSelectOption[]>(
        () => [
            {
                id: ChangeStatus.New,
                value: ChangeStatus.New,
                label: translate("llm.prompts.filters.change.new"),
            },
            {
                id: ChangeStatus.Lost,
                value: ChangeStatus.Lost,
                label: translate("llm.prompts.filters.change.lost"),
            },
        ],
        [],
    );

    const sentimentShiftOptions = useMemo<MultiSelectOption[]>(
        () => [
            {
                id: "Turned Positive",
                value: "Turned Positive",
                label: translate("llm.prompts.filters.sentiment_shift.turned_positive"),
            },
            {
                id: "Turned Negative",
                value: "Turned Negative",
                label: translate("llm.prompts.filters.sentiment_shift.turned_negative"),
            },
        ],
        [],
    );

    const trackFilter = (eventType: string) => (filterName: string) => {
        const labels: Record<string, string> = {
            mentionSentimentStatus: mentionStatusFilterLabel,
            visibility: visibilityFilterLabel,
            brands: brandsFilterLabel,
            domains: domainsFilterLabel,
            topSourceCategories: topSourceCategoryFilterLabel,
            citationGap: citationGapFilterLabel,
            citationVolatility: citationVolatilityFilterLabel,
            sentimentTone: sentimentFilterLabel,
            latestCitationStatus: latestCitationStatusFilterLabel,
            change: changeFilterLabel,
            sentimentShift: sentimentShiftFilterLabel,
        };

        if (eventType === "Apply" && filterName === "change") {
            const values = latestFilterStateRef.current.change;
            const suffix = values?.length ? `/${values.join(",")}` : "";
            allTrackers.dispatchTrackEvent("LLM Tracker - Prompts Filter", "Apply", `${changeFilterLabel}${suffix}`);
            return;
        }

        if (eventType === "Apply" && filterName === "sentimentShift") {
            const values = latestFilterStateRef.current.sentimentShift;
            const suffix = values?.length ? `/${values.join(",")}` : "";
            allTrackers.dispatchTrackEvent(
                "LLM Tracker - Prompts Filter",
                "Apply",
                `${sentimentShiftFilterLabel}${suffix}`,
            );
            return;
        }

        const label = labels[filterName];
        if (label) {
            allTrackers.dispatchTrackEvent("LLM Tracker - Prompts Filter", eventType, label);
        }
    };

    return (
        <Filters.Provider
            state={filterState}
            onChange={handleProviderChange}
            events={{
                onFilterClick: trackFilter("Click"),
                onFilterChange: trackFilter("Apply"),
                onFiltersClear: clearAllFilterParams,
            }}
        >
            <Filters.Range
                name="visibility"
                label={createRangeFilterLabel(visibilityFilterLabel, VISIBILITY_RANGE)}
                isDisabled={isLoading}
                properties={{
                    min: VISIBILITY_RANGE.from,
                    max: VISIBILITY_RANGE.to,
                }}
            />
            <Filters.MultiSelect
                name="mentionSentimentStatus"
                options={MENTION_STATUS_OPTIONS}
                width="250px"
                isDisabled={isLoading}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(mentionStatusFilterLabel, selectedText)}
            />
            <Filters.MultiSelect
                name="sentimentTone"
                options={SENTIMENT_TONE_OPTIONS}
                width="250px"
                isDisabled={isLoading}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(sentimentFilterLabel, selectedText)}
            />
            <BrandsFilter
                options={brandsOptions}
                selectedBrands={filterState.brands ?? []}
                coreCompetitorBrands={coreCompetitorBrands}
                isCoreCompetitorsSelected={isCoreCompetitorsSelected}
                onSelectionChange={handleBrandsSelectionChange}
                resolveLabel={createSelectorFilterLabel(brandsFilterLabel, selectedText)}
                onOpen={() => trackFilter("Click")("brands")}
                onApply={() => trackFilter("Apply")("brands")}
                isDisabled={isLoading}
            />
            <Filters.MultiSelect
                name="latestCitationStatus"
                options={LATEST_CITATION_STATUS_OPTIONS}
                width="200px"
                isDisabled={isLoading}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(latestCitationStatusFilterLabel, selectedText)}
            />
            <Filters.MultiSelect
                name="domains"
                placeholder={domainsPlaceholder}
                options={domainsOptions}
                width="250px"
                withSearch
                isDisabled={isLoading}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(domainsFilterLabel, selectedText)}
            />
            <Filters.MultiSelect
                name="topSourceCategories"
                placeholder={topSourceCategoryPlaceholder}
                options={TOP_SOURCE_CATEGORY_OPTIONS}
                width="250px"
                withSearch
                isDisabled={isLoading}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(topSourceCategoryFilterLabel, selectedText)}
            />
            <Filters.MultiSelect
                name="citationGap"
                options={CITATION_GAP_OPTIONS}
                width="200px"
                isDisabled={isLoading}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(citationGapFilterLabel, selectedText)}
            />
            <Filters.MultiSelect
                name="citationVolatility"
                options={CITATION_VOLATILITY_OPTIONS}
                width="200px"
                isDisabled={isLoading}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(citationVolatilityFilterLabel, selectedText)}
            />
            <Filters.MultiSelect
                name="change"
                options={changeOptions}
                width="200px"
                isDisabled={isLoading || !hasPriorWindow}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(changeFilterLabel, selectedText)}
            />
            <Filters.MultiSelect
                name="sentimentShift"
                options={sentimentShiftOptions}
                width="250px"
                isDisabled={isLoading || !hasPriorWindow}
                renderItem={({ label }) => <MultiselectFilterOption label={label} />}
                label={createSelectorFilterLabel(sentimentShiftFilterLabel, selectedText)}
            />
            <Filters.ClearAll />
        </Filters.Provider>
    );
};
